import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { CheckCircle2, Circle, Shield, Users, Zap } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DuckLoadingScreen } from '@/components/DuckLoadingScreen';
import '@/styles/login.css';

// Sexy ShadCN password requirements component
const PasswordRequirements = ({ password, confirmPassword }: { password: string; confirmPassword: string }) => {
  const requirements = [
    { label: 'At least 8 characters long', met: password.length >= 8 },
    { label: 'Contains at least one number', met: /\d/.test(password) },
    { label: 'Contains at least one uppercase letter', met: /[A-Z]/.test(password) },
    { label: 'Contains at least one lowercase letter', met: /[a-z]/.test(password) },
    { label: 'Contains at least one special character (!@#$%^&*(),.?":{}|<>_)', met: /[!@#$%^&*(),.?":{}|<>_]/.test(password) },
    { label: 'Passwords match', met: password.length > 0 && password === confirmPassword },
  ];

  const strengthRules = requirements.slice(0, 5);
  const metCount = strengthRules.filter((r) => r.met).length;
  const strengthPct = Math.round((metCount / strengthRules.length) * 100);
  const strengthLabel =
    metCount <= 1 ? 'Very weak' : metCount === 2 ? 'Weak' : metCount === 3 ? 'Fair' : metCount === 4 ? 'Good' : 'Strong';

  return (
    <Card aria-live="polite" className="w-full max-w-md mx-auto mt-2 mb-4 p-4 shadow-sm border bg-card/80 backdrop-blur">
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-medium text-muted-foreground">Password strength</span>
          <span
            className={cn(
              'text-sm font-medium',
              metCount < 3 ? 'text-amber-600' : metCount < 5 ? 'text-green-600' : 'text-green-700'
            )}
          >
            {strengthLabel}
          </span>
        </div>
        <Progress value={strengthPct} className="h-2" />
      </div>
      <ul className="space-y-2">
        {requirements.map((req, i) => (
          <li key={i} className="flex items-center gap-3 text-sm">
            {req.met ? (
              <CheckCircle2 className="w-5 h-5 text-green-600 transition-transform duration-200" />
            ) : (
              <Circle className="w-5 h-5 text-muted-foreground" />
            )}
            <span className={cn('transition-colors', req.met ? 'text-foreground' : 'text-muted-foreground')}>
              {req.label}
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
};


// Import Firebase configuration and authentication
import { auth, firebaseAuth, firebaseGetMultiFactorResolver } from '../../../js/external-js/config/main_config';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import 'firebase/compat/functions';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { validatePassword } from 'firebase/auth';

function getParameterByName(name) {
  name = name.replace(/\[/, '\\[').replace(/\]/, '\\]');
  var regexS = '[\\?&]' + name + '=([^&#]*)';
  var regex = new RegExp(regexS);
  var results = regex.exec(window.location.href);
  console.log(results);
  if (results == null) return '';
  else return decodeURIComponent(results[1].replace(/\+/g, ' '));
}


const AccountManagement = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);
  const [mode, setMode] = useState('');
  const [actionCode, setActionCode] = useState('');
  const [uid, setUid] = useState('');

  // const [searchParams] = useSearchParams();

  const [showPasswordReset, setShowPasswordReset] = useState(true);
  const [resetEmail, setResetEmail] = useState('');
  const [passwordWasReset, setPasswordWasReset] = useState(false);
  const [passwordWasSet, setPasswordWasSet] = useState(false);
  const [showMFA, setShowMFA] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [passwordInput1, setPasswordInput1] = useState('');
  const [passwordInput2, setPasswordInput2] = useState('');
  const [invalidMode, setInvalidMode] = useState(false);
  const [accessLoading, setAccessLoading] = useState(true);

  useEffect(() => {
    setAccessLoading(true);
    console.log("URL: " + window.location.href);
    const urlMode = getParameterByName('mode');
    const urlActionCode = getParameterByName('oobCode');
    const urlUid = getParameterByName('uid');

    setMode(urlMode);
    setActionCode(urlActionCode);
    setUid(urlUid);

    console.log("Mode: " + urlMode);
    console.log("Action Code: " + urlActionCode);
    console.log("UID: " + urlUid);

    // Validate oobCode based on mode and before loading the page
    switch (urlMode) {
      case 'resetPassword':
        auth.verifyPasswordResetCode(urlActionCode).then((email) => {
          setInvalidMode(false);
          setShowError(false);
          setResetEmail(email);
          setEmail(email);
          setMode('resetPassword');
          console.log('Email: ' + email);
          setAccessLoading(false);
        }).catch((error) => {
          console.log('Error: ' + error);
          setShowError(true);
          setMode('');
          setErrorMessage(error.message);
          setInvalidMode(true);
          setAccessLoading(false);
        });
        break;
      case 'recoverEmail':
        auth.verifyPasswordResetCode(urlActionCode).then((email) => {
          setInvalidMode(false);
          setShowError(false);
          setResetEmail(email);
          setEmail(email);
          setMode('recoverEmail');
          console.log('Email: ' + email);
          setAccessLoading(false);
        }).catch((error) => {
          console.log('Error: ' + error);
          setShowError(true);
          setMode('');
          setErrorMessage(error.message);
          setInvalidMode(true);
          setAccessLoading(false);
        });
        break;
      case 'verifyEmail':
        auth.verifyPasswordResetCode(urlActionCode).then((email) => {
          setInvalidMode(false);
          setShowError(false);
          setResetEmail(email);
          setEmail(email);
          setMode('verifyEmail');
          console.log('Email: ' + email);
          setAccessLoading(false);
        }).catch((error) => {
          console.log('Error: ' + error);
          setShowError(true);
          setMode('');
          setErrorMessage(error.message);
          setInvalidMode(true);
          setAccessLoading(false);
        });
        break;
      case 'setUserPassword':
        setInvalidMode(false);
        setShowError(false);
        setResetEmail(email);
        setEmail(email);
        setMode('setUserPassword');
        console.log('Email: ' + email);
        setAccessLoading(false);
        break;
      default:
        console.log('Invalid mode');
        setMode('');
        setShowError(false);
        // setErrorMessage('Invalid mode');
        setInvalidMode(true);
        setAccessLoading(false);
        break;
    }
  }, []);

  const handlePasswordReset = async () => {
    // Check if it passes the password requirements
    if (!validatePassword(auth, passwordInput1)) {
      setShowError(true);
      setErrorMessage('Password does not meet requirements');
      setPasswordWasReset(false);
      setIsLoading(false);
      return;
    }

    // Reset password
    await auth.confirmPasswordReset(actionCode, passwordInput1);
    setPasswordWasReset(true);

    navigate('/login');

  }

  const handleSetInitialPassword = async () => {
    setIsLoading(true);
    try {
      // Get the password history document from Firestore
      const historyDocRef = firebase.firestore().collection('passwordSetHistory').doc('history');
      const historyDoc = await historyDocRef.get();
      
      if (historyDoc.exists && historyDoc.data()[actionCode]) {
        const expired = historyDoc.data()[actionCode].actionCodeExpired;
        console.log('Action code expired status:', expired);
        
        if (expired === true) {
          // This link has already been used
          setShowError(true);
          setErrorMessage('Sorry! This link has already been used to set a password for this account. Please request a reset password link.');
          setPasswordWasSet(false);
        } else if (expired === false) {
          // Check password requirements
          if (!validatePassword(auth, passwordInput1) || passwordInput1 !== passwordInput2) {
            setShowError(true);
            setErrorMessage('Password does not meet requirements or passwords do not match');
            setPasswordWasSet(false);
            return;
          }
          
          // Get the correct region based on environment
          const region = import.meta.env.VITE_REGION || 'au';
          const functionsRegion = region === 'eu' ? 'europe-west4' : 'australia-southeast1';
          console.log(`Using ${functionsRegion} region`);
          
          // Get a Functions instance for the specific region
          // This is the correct syntax for firebase/compat
          const regionFunctions = firebase.app().functions(functionsRegion);
          
          // Create the callable function
          const setInitialPassword = regionFunctions.httpsCallable('setInitialPassword');
          await setInitialPassword({
            actionCode: actionCode,
            uid: uid,
            newPassword: passwordInput1
          });
          
          setPasswordWasSet(true);
          setShowError(false);
          setTimeout(() => navigate('/login'), 2000);
        } else {
          // Unknown state
          setShowError(true);
          setErrorMessage('Invalid password reset link. Please request a new one.');
          setPasswordWasSet(false);
        }
      } else {
        setShowError(true);
        setErrorMessage('Invalid password reset link. This link may not exist or might have expired.');
        setPasswordWasSet(false);
      }
    } catch (error) {
      console.error('Error setting password:', error);
      setShowError(true);
      setErrorMessage(error.message || 'An error occurred while setting your password');
      setPasswordWasSet(false);
    } finally {
      setIsLoading(false);
    }
  }

  const duckFacts = [
    "Quack! Getting your account ready...",
    "Waddling through the verification process...",
    "Fetching the freshest security breadcrumbs...",
    "Paddling over to confirm your email...",
    "Lining up all your login ducks...",
    "Smoothly floating through password reset...",
    "Making sure your details are in tip-top shape...",
    "Checking the nest for your recovery email...",
    "Fluffing up your account settings...",
    "Giving your MFA a gentle preen...",
    "Ensuring safe waters before you sign in...",
    "Gliding through the setup process...",
    "Polishing your profile feathers...",
    "Tucking in new credentials nice and safe...",
    "Happily quacking towards completion..."
  ];


  if (accessLoading) {
    return <DuckLoadingScreen title="Loading" text="Please wait while we validate your request." duckFacts={duckFacts} />;
  }

  return (
    <div className="auth-container">
      {/* Hidden reCAPTCHA container for MFA */}
      <div id="recaptcha-container" style={{ display: 'none' }}></div>
      {/* Desktop view - two column layout */}
      <div className="desktop-view">
        {/* Animated gradient background - spans both columns */}
        <div className="gradient-overlay"></div>
        <div className="glow-1"></div>
        <div className="glow-2"></div>
        
        {/* Left column - Branding panel */}
        <div className="left-column">
          {/* Content - centered vertically like Controlyze */}
          <div className="left-content">
            {/* Duck mascot and branding */}
            <div className="logo-container">
              <div className="logo-glow"></div>
              <img 
                src="/assets/ducks/sunglasses_left.svg" 
                alt="de.iterate Duck" 
                className="brand-logo" 
              />
              <h1 className="brand-title">de.iterate</h1>
              <p className="brand-tagline">Your GRC platform, simplified.</p>
            </div>
            
            {/* Feature cards */}
            <div className="feature-cards">
              <div className="feature-card">
                <div className="feature-icon">
                  <Shield />
                </div>
                <div className="feature-content">
                  <h3>Security First</h3>
                  <p>Enterprise-grade security for your compliance needs</p>
                </div>
              </div>
              <div className="feature-card">
                <div className="feature-icon">
                  <Users />
                </div>
                <div className="feature-content">
                  <h3>Team Collaboration</h3>
                  <p>Work together seamlessly across your organization</p>
                </div>
              </div>
              <div className="feature-card">
                <div className="feature-icon">
                  <Zap />
                </div>
                <div className="feature-content">
                  <h3>Automated Workflows</h3>
                  <p>Streamline your GRC processes with automation</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right column - reset password form - after email confirmation - 
        this is the page they get brought to from the link in the email */}
        <div className="right-column">
          <div className="auth-form-container">

            {/* Error message */}
            {/* {invalidMode && <div className="error-message">Invalid mode</div>} */}
            {showError && <div className="error-message">{errorMessage}</div>}

            {!showError && mode === 'resetPassword' ? (
              <>
                <div className="auth-header">
                  <h1>Reset Password</h1>
                  <p>Enter your new password below to reset your password</p>
                </div>

                <div className="password-reset-form">
                  {showError && <div className="error-message">{errorMessage}</div>}
                  <div className="form-group">
                    <Input
                      id="passwordInput1"
                      placeholder="Password"
                      type="password"
                      autoCapitalize="none"
                      autoComplete="password"
                      autoCorrect="off"
                      disabled={isLoading || passwordWasReset}
                      value={passwordInput1}
                      onChange={(e) => setPasswordInput1(e.target.value)}
                      className="mb-4"
                    />
                  </div>
                  <div className="form-group">
                    <Input
                      id="passwordInput2"
                      placeholder="Confirm Password"
                      type="password"
                      autoCapitalize="none"
                      autoComplete="password"
                      autoCorrect="off"
                      disabled={isLoading || passwordWasReset}
                      value={passwordInput2}
                      onChange={(e) => setPasswordInput2(e.target.value)}
                      className="mb-4"
                    />
                  </div>
                  {/* Beautiful password requirements */}
                  {/* Sexy ShadCN-powered password requirements */}
                  <PasswordRequirements
                    password={passwordInput1}
                    confirmPassword={passwordInput2}
                  />
                  <div className="form-actions">
                    <Button className="reset-button" disabled={isLoading || passwordWasReset} onClick={handlePasswordReset}>
                      {isLoading && (
                        <svg
                          className="spinner-icon"
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round">
                          <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                        </svg>
                      )}
                      {passwordWasReset ? 'Password Reset' : 'Reset Password'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowPasswordReset(false);
                        setShowError(false);
                      }}
                      disabled={isLoading}>
                      Back to Login
                    </Button>
                  </div>
                </div>
              </>
            ) : !showError && mode === 'setUserPassword' ? (
              <>
                <div className="auth-header">
                  <h1>Set Password</h1>
                  <p>Enter your new password below to set your password</p>
                </div>

                <div className="password-reset-form">
                  {showError && <div className="error-message">{errorMessage}</div>}
                  <div className="form-group">
                    <Input
                      id="passwordInput1"
                      placeholder="Password"
                      type="password"
                      autoCapitalize="none"
                      autoComplete="password"
                      autoCorrect="off"
                      disabled={isLoading || passwordWasSet}
                      value={passwordInput1}
                      onChange={(e) => setPasswordInput1(e.target.value)}
                      className="mb-4"
                    />
                  </div>
                  <div className="form-group">
                    <Input
                      id="passwordInput2"
                      placeholder="Confirm Password"
                      type="password"
                      autoCapitalize="none"
                      autoComplete="password"
                      autoCorrect="off"
                      disabled={isLoading || passwordWasSet}
                      value={passwordInput2}
                      onChange={(e) => setPasswordInput2(e.target.value)}
                      className="mb-4"
                    />
                  </div>
                  {/* Beautiful password requirements */}
                  {/* Sexy ShadCN-powered password requirements */}
                  <PasswordRequirements
                    password={passwordInput1}
                    confirmPassword={passwordInput2}
                  />
                  <div className="form-actions">
                    <Button className="reset-button" disabled={isLoading || passwordWasSet} onClick={handleSetInitialPassword}>
                      {isLoading && (
                        <svg
                          className="spinner-icon"
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round">
                          <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                        </svg>
                      )}
                      {passwordWasSet ? 'Password Set' : 'Set Password'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowPasswordReset(false);
                        setShowError(false);
                      }}
                      disabled={isLoading}>
                      Back to Login
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="auth-form">
                <div className="auth-header">
                  <h1>Access Denied</h1>
                  <p>You do not have access to this page</p>
                </div>
              </div>
            )}

          </div>
        </div >
      </div >

      {/* Mobile view */}
      <div className="mobile-view">
        <img src="/assets/img/logo.png" alt="de.iterate Logo" className="auth-logo mobile-logo" />
        <div className="mobile-form-container">


          <p className="mobile-terms-text">
            By clicking continue, you agree to our{' '}
            <a href="/terms" className="terms-link">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="/privacy" className="terms-link">
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  );
};

export default AccountManagement;
